import { Link } from "wouter";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme-context";
import { useGetSettings } from "@workspace/api-client-react";
import { Mail, CheckCircle, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { PlatformBadge, PlatformProviderJsonLd } from "./PlatformBadge";
import { PaymentBrand, PAYMENT_BRAND_CATALOG, type PaymentBrandKey } from "./PaymentBrand";
import { resolveImageSrc } from "@/lib/imgSrc";

interface PaymentMethodEntry {
  key: PaymentBrandKey | string;
  imageUrl?: string;
  label?: string;
  enabled?: boolean;
}

const DEFAULT_PAYMENT_METHODS: PaymentMethodEntry[] = [
  { key: "mada",       enabled: true },
  { key: "visa",       enabled: true },
  { key: "mastercard", enabled: true },
  { key: "applepay",   enabled: true },
  { key: "stcpay",     enabled: true },
];

function parsePaymentMethods(raw: unknown): PaymentMethodEntry[] {
  if (!raw) return DEFAULT_PAYMENT_METHODS;
  let value: unknown = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "[]") return DEFAULT_PAYMENT_METHODS;
    try { value = JSON.parse(t); } catch { return DEFAULT_PAYMENT_METHODS; }
  }
  if (!Array.isArray(value)) return DEFAULT_PAYMENT_METHODS;
  const cleaned = value
    .filter((it): it is PaymentMethodEntry => !!it && typeof it === "object" && typeof (it as any).key === "string")
    .map(it => ({ key: it.key, imageUrl: it.imageUrl || undefined, label: it.label || undefined, enabled: it.enabled !== false }));
  return cleaned.length ? cleaned : DEFAULT_PAYMENT_METHODS;
}

function clampNum(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export const SOCIAL_ICON_PATHS: Record<string, string> = {
  facebook: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  instagram: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
  twitter: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  youtube: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  tiktok: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  snapchat: "M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.076-.375-.09-.84-.181-1.44-.181-.535 0-1.064.091-1.574.27-.51.166-1.005.465-1.409.795-.899.735-1.544 1.006-2.099 1.006-.539 0-1.184-.271-2.084-1.006-.404-.33-.899-.629-1.409-.795-.51-.181-1.039-.27-1.574-.27-.6 0-1.064.091-1.44.18-.225.044-.404.075-.539.075h-.015c-.299 0-.494-.135-.569-.42-.061-.18-.105-.375-.135-.554-.044-.194-.104-.479-.164-.57-1.873-.283-2.906-.702-3.146-1.271-.03-.075-.046-.149-.046-.225-.015-.239.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.135-.045-.255-.09-.345-.12-.659-.254-1.24-.644-1.228-1.153 0-.359.3-.704.749-.853.135-.06.313-.09.494-.09.165 0 .3.015.449.09.374.165.718.285 1.049.301.165 0 .3-.046.389-.09-.014-.18-.029-.36-.044-.54-.3-1.628-.435-3.654.09-4.848C7.847 1.069 11.216.793 12.206.793z",
  telegram: "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z",
  pinterest: "M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z",
  linkedin: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
};

interface SocialIconConfig {
  size: number;
  shape: string;
  color: string;
  hoverColor: string;
  bg: string;
  catalog: Array<{ type: string; slug: string; imageUrl: string }>;
}

// Map admin social-platform keys (which mirror the legacy theme columns
// like socialTwitter / socialFacebook …) to the canonical slugs used by
// the platform-managed brand-asset catalog. Today the only divergence is
// "twitter" → "x", but we keep this as an explicit table so future
// renames stay in one place.
const SOCIAL_CATALOG_SLUG: Record<string, string> = { twitter: "x" };

const SocialIcon = ({ type, url, cfg }: { type: string; url: string; cfg: SocialIconConfig }) => {
  if (!url) return null;
  const catSlug = SOCIAL_CATALOG_SLUG[type] || type;
  const catalogEntry = cfg.catalog.find((c) => c.type === "social" && c.slug === catSlug);
  const catalogUrl = catalogEntry?.imageUrl || "";
  const baseSize = cfg.size;

  const shaped = cfg.shape && cfg.shape !== "default";
  const padding = shaped ? Math.max(6, Math.round(baseSize * 0.4)) : 0;
  const wrapperSize = baseSize + padding * 2;
  const radius =
    cfg.shape === "circle" ? "9999px" :
    cfg.shape === "rounded" ? "8px" :
    cfg.shape === "square" ? "0px" : "0px";

  const wrapperStyle: React.CSSProperties = shaped
    ? {
        width: wrapperSize,
        height: wrapperSize,
        borderRadius: radius,
        backgroundColor: cfg.bg || "rgba(255,255,255,0.08)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }
    : { display: "inline-flex" };

  // Prefer the inline themed SVG when available — it colors with the theme
  // (cfg.color) and never 404s. Fall back to the platform catalog <img> only
  // when we don't have a path for this social type.
  const inlinePath = SOCIAL_ICON_PATHS[type];
  const inner = inlinePath ? (
    <svg
      width={baseSize}
      height={baseSize}
      fill="currentColor"
      viewBox="0 0 24 24"
      style={{ color: cfg.color }}
    >
      <path d={inlinePath} />
    </svg>
  ) : catalogUrl ? (
    <img
      src={catalogUrl}
      alt={type}
      loading="lazy"
      decoding="async"
      style={{ width: baseSize, height: baseSize, objectFit: "contain" }}
    />
  ) : null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="transition-colors social-icon-link"
      aria-label={type}
      style={{ ...wrapperStyle, color: cfg.color, ["--social-hover" as any]: cfg.hoverColor }}
    >
      {inner}
    </a>
  );
};

export interface FooterSection {
  id: string;
  titleAr: string;
  titleEn: string;
  links: { labelAr: string; labelEn: string; href: string }[];
}

export const DEFAULT_FOOTER_SECTIONS: FooterSection[] = [
  {
    id: "company",
    titleAr: "معلومات المتجر",
    titleEn: "Store Info",
    links: [
      { labelAr: "عن المتجر", labelEn: "About Us", href: "/page/about-us" },
      { labelAr: "طرق الشحن", labelEn: "Shipping Methods", href: "/page/shipping" },
      { labelAr: "طرق الدفع", labelEn: "Payment Methods", href: "/page/payment-methods" },
      { labelAr: "سياسة الخصوصية", labelEn: "Privacy Policy", href: "/privacy-policy" },
      { labelAr: "الشروط والاحكام للموقع", labelEn: "Terms & Conditions", href: "/terms-of-service" },
      { labelAr: "شروط وأحكام الشراء", labelEn: "Purchase Terms", href: "/page/purchase-terms" },
      { labelAr: "سياسة الاستبدال والاسترجاع", labelEn: "Returns & Exchanges", href: "/page/returns" },
      { labelAr: "التراخيص والضريبة", labelEn: "Licenses & Tax", href: "/page/licenses" },
    ],
  },
  {
    id: "account",
    titleAr: "حسابي",
    titleEn: "My Account",
    links: [
      { labelAr: "تسجيل الدخول", labelEn: "Login", href: "/login" },
      { labelAr: "طلباتي", labelEn: "My Orders", href: "/account?tab=orders" },
      { labelAr: "المفضلة", labelEn: "Wishlist", href: "/wishlist" },
      { labelAr: "العناوين", labelEn: "Addresses", href: "/account?tab=addresses" },
    ],
  },
  {
    id: "support",
    titleAr: "المساعدة والدعم الفني",
    titleEn: "Help & Support",
    links: [
      { labelAr: "الأسئلة الشائعة", labelEn: "FAQ", href: "/faq" },
      { labelAr: "تواصل معنا", labelEn: "Contact Us", href: "/contact" },
      { labelAr: "تتبع الطلب", labelEn: "Track Order", href: "/order-tracking" },
    ],
  },
  {
    id: "more",
    titleAr: "المزيد من الصفحات",
    titleEn: "More Pages",
    links: [
      { labelAr: "المدونة", labelEn: "Blog", href: "/blog" },
      { labelAr: "الأعراس", labelEn: "Weddings", href: "/page/weddings" },
      { labelAr: "هدايا الشركات", labelEn: "Corporate Gifts", href: "/page/corporate-gifts" },
    ],
  },
];

function parseFooterSections(raw: unknown): FooterSection[] {
  if (!raw) return DEFAULT_FOOTER_SECTIONS;
  let value: unknown = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "[]") return DEFAULT_FOOTER_SECTIONS;
    try { value = JSON.parse(t); } catch { return DEFAULT_FOOTER_SECTIONS; }
  }
  if (!Array.isArray(value) || !value.length) return DEFAULT_FOOTER_SECTIONS;
  return value as FooterSection[];
}

function LicenseTag({
  label, value, verifyUrl, ar,
}: { label: string; value: string; verifyUrl?: string; ar: boolean }) {
  const [qr, setQr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || qr || !verifyUrl) return;
    let cancelled = false;
    // Lazy-load qrcode so the storefront critical path doesn't pay for it
    // unless a customer actually clicks Verify.
    import("qrcode").then((mod) => {
      if (cancelled) return;
      mod.toDataURL(verifyUrl, { margin: 1, width: 160 })
        .then((d) => { if (!cancelled) setQr(d); })
        .catch(() => { /* non-critical */ });
    }).catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [open, qr, verifyUrl]);

  const body = (
    <>
      <span className="font-semibold">{label}:</span>{" "}
      <span dir="ltr">{value}</span>
    </>
  );

  if (!verifyUrl) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-white/70 px-2.5 py-1 ring-1 ring-border text-xs">
        {body}
      </span>
    );
  }

  return (
    <span className="relative inline-flex items-center gap-1 rounded-md bg-white/70 px-2.5 py-1 ring-1 ring-border text-xs">
      <a href={verifyUrl} target="_blank" rel="noopener noreferrer" title={ar ? "تحقق من المصدر الرسمي" : "Verify with official source"}>
        {body}
      </a>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-sm hover:bg-black/5 text-muted-foreground"
        aria-label={ar ? "رمز التحقق" : "Verification QR"}
        title={ar ? "اعرض رمز QR للتحقق" : "Show verification QR"}
      >
        {/* tiny QR glyph */}
        <svg viewBox="0 0 16 16" className="w-3 h-3" fill="currentColor" aria-hidden="true">
          <path d="M0 0h6v6H0V0zm2 2v2h2V2H2zm8-2h6v6h-6V0zm2 2v2h2V2h-2zM0 10h6v6H0v-6zm2 2v2h2v-2H2zm8-2h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
        </svg>
      </button>
      {open && (
        <span
          role="dialog"
          aria-label={ar ? "رمز التحقق" : "Verification QR"}
          className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-xl border p-2 flex flex-col items-center"
          style={{ minWidth: 180 }}
        >
          {qr
            ? <img src={qr} alt={`QR ${label} ${value}`} width={160} height={160} />
            : <span className="text-[10px] text-muted-foreground p-8">…</span>}
          <span className="text-[10px] mt-1 text-muted-foreground text-center" dir={ar ? "rtl" : "ltr"}>
            {ar ? "امسح للتحقق رسمياً" : "Scan to verify officially"}
          </span>
        </span>
      )}
    </span>
  );
}

interface TrustInfo {
  enabled: boolean;
  maroof: { id: string | null; url: string | null; verified: boolean } | null;
  licenses: {
    mocLicense: string | null;
    gastat: string | null;
    sfda: string | null;
    vat: string | null;
    cr: string | null;
  };
}

function TrustStrip({ lang }: { lang: string }) {
  const ar = lang === "ar";
  const [info, setInfo] = useState<TrustInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "") + "/api";
    fetch(`${base}/trust/info`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setInfo(d); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, []);
  if (!info || !info.enabled) return null;
  const m = info.maroof;
  const l = info.licenses;
  const items: React.ReactNode[] = [];
  if (m && (m.url || m.id)) {
    items.push(
      <a
        key="maroof"
        href={m.url || `https://maroof.sa/${m.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 transition"
        title={ar ? "تحقق من ملف معروف" : "Verify on Maroof"}
        data-testid="trust-maroof"
      >
        <ShieldCheck className="w-4 h-4" />
        <span>{ar ? "موثّق في" : "Listed on"} <strong>معروف</strong></span>
        {m.verified && <CheckCircle className="w-3.5 h-3.5" aria-label={ar ? "موثّق" : "verified"} />}
      </a>
    );
  }
  const tag = (key: string, label: string, value: string, verifyUrl?: string) => (
    <LicenseTag
      key={key}
      label={label}
      value={value}
      verifyUrl={verifyUrl}
      ar={ar}
    />
  );
  // Government verification portals (KSA). Where a portal accepts a URL
  // query, we link directly to a pre-filled search so customers can confirm
  // the merchant's claim against the source registry. The QR popover in
  // LicenseTag exposes the same URL for in-person scanning.
  if (l.mocLicense) items.push(tag("moc", ar ? "وزارة التجارة" : "MoC", l.mocLicense, `https://mc.gov.sa/ar/eservices/Pages/ServiceDetails.aspx?param=${encodeURIComponent(l.mocLicense)}`));
  if (l.gastat)     items.push(tag("gastat", ar ? "الإحصاء" : "GASTAT", l.gastat, `https://portal.saudicensus.sa/portal`));
  if (l.sfda)       items.push(tag("sfda", ar ? "الغذاء والدواء" : "SFDA", l.sfda, `https://www.sfda.gov.sa/ar/searchestablishment?keyword=${encodeURIComponent(l.sfda)}`));
  if (l.vat)        items.push(tag("vat", ar ? "الرقم الضريبي" : "VAT", l.vat, `https://zatca.gov.sa/ar/eServices/Pages/TaxpayerLookup.aspx?vat=${encodeURIComponent(l.vat)}`));
  if (l.cr)         items.push(tag("cr", ar ? "السجل التجاري" : "CR", l.cr, `https://eservices.mci.gov.sa/eservices/myCommerce/${encodeURIComponent(l.cr)}`));
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 pt-4 pb-2 text-sm" data-testid="trust-strip">
      {items}
    </div>
  );
}

export function StoreFooter() {
  const { t, lang, setLang } = useI18n();
  const theme = useTheme();
  const { data: settings } = useGetSettings();
  const storeName = (settings as any)?.storeName || (lang === "ar" ? "المتجر" : "Store");
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const API_BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "") + "/api";
  const [brandCatalog, setBrandCatalog] = useState<Array<{ type: string; slug: string; labelAr: string; labelEn: string; imageUrl: string; linkPattern: string | null }>>([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/brand-assets`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(j => { if (!cancelled && Array.isArray(j?.items)) setBrandCatalog(j.items); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [API_BASE]);

  const handleSubscribe = async () => {
    if (!email || !email.includes("@")) return;
    setSubscribeStatus("loading");
    try {
      const res = await fetch(`${API_BASE}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setSubscribeStatus("success");
        setEmail("");
        setTimeout(() => setSubscribeStatus("idle"), 4000);
      } else {
        setSubscribeStatus("error");
        setTimeout(() => setSubscribeStatus("idle"), 3000);
      }
    } catch {
      setSubscribeStatus("error");
      setTimeout(() => setSubscribeStatus("idle"), 3000);
    }
  };

  const toggleSection = (id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const t_ = theme as any;
  const footerBg = t_?.footerBg || "#d4cec3";
  const footerText = t_?.footerTextColor || "#1a1a1a";
  const linkColor = t_?.footerLinkColor || "#1a1a1a";
  const linkHoverColor = t_?.footerLinkHoverColor || "#555555";

  const socialLinks = [
    { type: "instagram", url: theme?.socialInstagram },
    { type: "facebook", url: theme?.socialFacebook },
    { type: "twitter", url: theme?.socialTwitter },
    { type: "youtube", url: theme?.socialYoutube },
    { type: "linkedin", url: theme?.socialLinkedin },
    { type: "tiktok", url: theme?.socialTiktok },
    { type: "snapchat", url: theme?.socialSnapchat },
    { type: "telegram", url: theme?.socialTelegram },
    { type: "pinterest", url: theme?.socialPinterest },
  ].filter(s => s.url);

  const socialCfg: SocialIconConfig = {
    size: Math.max(12, Math.min(64, Number(t_?.socialIconSize) || 20)),
    shape: t_?.socialIconShape || "circle",
    color: t_?.socialIconColor || "#ffffff",
    hoverColor: t_?.socialIconHoverColor || "#ffffff",
    bg: t_?.socialIconBg || "#dc2626",
    catalog: brandCatalog,
  };
  const showSocial = theme?.footerSocialLinks !== "false";
  const showNewsletter = t_?.footerNewsletter !== "false";

  const nlBg = t_?.newsletterBg || `color-mix(in srgb, ${footerBg} 85%, black)`;
  const nlTitleColor = t_?.newsletterTitleColor || footerText;
  const nlTextColor = t_?.newsletterTextColor || footerText;
  const nlButtonBg = t_?.newsletterButtonBg || "#b91c1c";
  const nlButtonHoverBg = t_?.newsletterButtonHoverBg || "#991b1b";
  const nlButtonText = t_?.newsletterButtonText || "#ffffff";
  const nlShape = t_?.newsletterShape || "pill";
  const nlRadiusClass = nlShape === "square" ? "rounded-none" : nlShape === "rounded" ? "rounded-lg" : "rounded-full";
  // Honor the site-wide border-radius first so the unified "Global Radius"
  // appearance control cascades to the newsletter pill the same way it
  // already cascades to buttons/cards/banners. Falls back to the dedicated
  // newsletter shape preset only when the merchant hasn't set a global value.
  const nlRadiusStyle: React.CSSProperties = theme?.borderRadius
    ? { borderRadius: theme.borderRadius }
    : {};
  const nlTitle = lang === "ar"
    ? (t_?.newsletterTitleAr || "اشترك في نشرتنا البريدية")
    : (t_?.newsletterTitleEn || "Subscribe to our newsletter");
  const nlSubtitle = lang === "ar"
    ? (t_?.newsletterSubtitleAr || "احصل على أحدث العروض والمنتجات الجديدة مباشرة في بريدك")
    : (t_?.newsletterSubtitleEn || "Get the latest offers and new products directly in your inbox");
  const nlButtonLabel = lang === "ar"
    ? (t_?.newsletterButtonLabelAr || "اشتراك")
    : (t_?.newsletterButtonLabelEn || "Subscribe");

  const footerSections = parseFooterSections(t_?.footerSections);

  const description = lang === "ar"
    ? (t_?.footerDescriptionAr || "")
    : (t_?.footerDescriptionEn || "");

  const serviceTitle = lang === "ar"
    ? (t_?.footerServiceTitleAr || "خدمة العملاء")
    : (t_?.footerServiceTitleEn || "Customer Service");
  const serviceContent = lang === "ar"
    ? (t_?.footerServiceContentAr || "")
    : (t_?.footerServiceContentEn || "");

  const copyrightText = lang === "ar"
    ? (theme?.footerCopyrightAr || `© ${new Date().getFullYear()} ${storeName}. جميع الحقوق محفوظة`)
    : (theme?.footerCopyright || `© ${new Date().getFullYear()} ${storeName}. All rights reserved`);

  const separatorColor = `color-mix(in srgb, ${footerText} 25%, transparent)`;

  return (
    <footer dir={lang === "ar" ? "rtl" : "ltr"} style={{ backgroundColor: footerBg, color: footerText }}>
      <style>{`.footer-link{color:${linkColor};text-decoration:none;transition:color 0.2s}.footer-link:hover{color:${linkHoverColor}}`}</style>

      {showNewsletter && (
        <div style={{ backgroundColor: nlBg, color: nlTextColor }}>
          <style>{`.nl-btn{background-color:${nlButtonBg};color:${nlButtonText}}.nl-btn:hover:not(:disabled){background-color:${nlButtonHoverBg}}`}</style>
          <div className="max-w-2xl mx-auto px-6 py-8 text-center">
            <h3 className="text-base font-bold mb-1" style={{ color: nlTitleColor }}>
              {nlTitle}
            </h3>
            <p className="text-sm mb-4">
              {nlSubtitle}
            </p>
            {subscribeStatus === "success" ? (
              <div className="inline-flex items-center gap-2 bg-green-700/20 border border-green-700/40 rounded-lg px-5 py-3">
                <CheckCircle className="h-5 w-5 text-green-700" />
                <span className="text-sm font-medium">{lang === "ar" ? "تم الاشتراك بنجاح!" : "Subscribed successfully!"}</span>
              </div>
            ) : subscribeStatus === "error" ? (
              <div className="inline-flex items-center gap-2 bg-red-700/20 border border-red-700/40 rounded-lg px-5 py-3">
                <Mail className="h-5 w-5 text-red-700" />
                <span className="text-sm font-medium">{lang === "ar" ? "حدث خطأ، حاول مرة أخرى" : "Something went wrong"}</span>
              </div>
            ) : (
              <div
                className={`flex max-w-md mx-auto ${theme?.borderRadius ? "" : nlRadiusClass} overflow-hidden shadow-sm`}
                style={nlRadiusStyle}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                  placeholder={lang === "ar" ? "اكتب بريدك الإلكتروني" : "Enter your email"}
                  className="flex-1 h-11 px-5 bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none min-w-0 border-0"
                  dir="ltr"
                  disabled={subscribeStatus === "loading"}
                />
                <button
                  onClick={handleSubscribe}
                  disabled={subscribeStatus === "loading" || !email.includes("@")}
                  className="nl-btn h-11 px-6 disabled:opacity-60 transition-colors flex items-center gap-2 text-sm font-bold whitespace-nowrap"
                >
                  {subscribeStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  <span>{nlButtonLabel}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-2xl md:max-w-6xl mx-auto px-6 py-10">
        <div className="flex justify-center gap-4 text-sm mb-8">
          <button
            onClick={() => setLang("ar")}
            className={`transition-colors ${lang === "ar" ? "font-bold underline underline-offset-4" : "hover:underline hover:underline-offset-4"}`}
          >
            العربية
          </button>
          <button
            onClick={() => setLang("en")}
            className={`transition-colors ${lang === "en" ? "font-bold underline underline-offset-4" : "hover:underline hover:underline-offset-4"}`}
          >
            English
          </button>
        </div>

        {(() => {
          // Mutually-exclusive brand block: show the logo image when one
          // is configured (and the merchant didn't explicitly hide it),
          // otherwise fall back to the store name. Never both.
          const rawLogo = t_?.footerLogoUrl || t_?.logoUrl;
          const logoUrl = typeof rawLogo === "string" ? rawLogo.trim() : "";
          const hasLogo = !!logoUrl && t_?.footerShowLogo !== "false";
          if (hasLogo) {
            return (
              <div className="text-center mb-2">
                {/* CLS fix: reserve a fixed-aspect 120x40 box so the
                    layout doesn't shift when the image decodes. */}
                <div
                  style={{ width: 120, height: 40, marginLeft: "auto", marginRight: "auto" }}
                  className="mb-3"
                >
                  <img
                    src={(resolveImageSrc(logoUrl, 240) as string) || logoUrl}
                    alt={storeName}
                    width={120}
                    height={40}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            );
          }
          return <h2 className="text-2xl font-bold text-center mb-4">{storeName}</h2>;
        })()}

        {description && (
          <p className="text-sm text-center leading-relaxed opacity-95 mb-6 max-w-lg mx-auto">
            {description}
          </p>
        )}

        {showSocial && socialLinks.length > 0 && (
          <div className="text-center mb-8">
            <p className="text-sm font-semibold mb-4">
              {lang === "ar" ? "تابعنا على" : "Follow us on"}
            </p>
            <div className="flex justify-center items-center gap-3">
              {socialLinks.map(s => <SocialIcon key={s.type} type={s.type} url={s.url!} cfg={socialCfg} />)}
            </div>
          </div>
        )}

        {/* Sections list.
            Mobile (default): vertical accordion with +/− toggles, top borders,
            collapsed content. Same UX as before.
            Desktop (md+): all sections rendered side-by-side as columns,
            every list expanded, no toggle, no borders. The grid column count
            adapts to how many sections the merchant configured (1..4) so
            short footers don't stretch awkwardly across the whole row. */}
        <div className={`md:grid md:gap-8 md:items-start ${
          footerSections.length >= 4 ? "md:grid-cols-4"
          : footerSections.length === 3 ? "md:grid-cols-3"
          : footerSections.length === 2 ? "md:grid-cols-2"
          : "md:grid-cols-1"
        }`}>
          {footerSections.map((section, idx) => {
            const isOpen = openSections.has(section.id);
            return (
              <div
                key={section.id}
                style={{ borderTop: `1px solid ${separatorColor}` }}
                className="md:!border-t-0"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center justify-between w-full py-4 text-base font-bold md:cursor-default md:py-2 md:text-start md:pointer-events-none"
                  style={{ color: footerText }}
                >
                  <span>{lang === "ar" ? section.titleAr : section.titleEn}</span>
                  <span className="text-lg leading-none select-none md:hidden">{isOpen ? "—" : "+"}</span>
                </button>
                <div className={`${isOpen ? "block" : "hidden"} md:block pb-4 space-y-3`}>
                  {section.links.map((link, li) => {
                    const href = /^(\/|https?:\/\/)/i.test(link.href) ? link.href : "#";
                    return (
                      <div key={li}>
                        <Link href={href} className="footer-link text-sm">
                          {lang === "ar" ? link.labelAr : link.labelEn}
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ borderTop: `1px solid ${separatorColor}` }} className="md:hidden" />
        </div>

        {serviceContent && (
          <div className="text-center pt-6 pb-2">
            <h3 className="text-base font-bold mb-2">{serviceTitle}</h3>
            <p className="text-sm opacity-95 whitespace-pre-line leading-relaxed">{serviceContent}</p>
          </div>
        )}

        {theme?.footerPaymentIcons !== "false" && (() => {
          const methods = parsePaymentMethods(theme?.footerPaymentMethods).filter(m => m.enabled !== false);
          if (!methods.length) return null;
          const iconSize = clampNum(theme?.footerPaymentIconSize, 28, 14, 96);
          const gap = Math.max(6, Math.round(iconSize * 0.32));
          return (
            <div className="flex flex-wrap items-center justify-center pt-2 pb-4" style={{ gap }}>
              {methods.map((m, idx) => {
                const meta = PAYMENT_BRAND_CATALOG.find(b => b.key === m.key);
                const catalogEntry = brandCatalog.find((c) => c.type === "payment" && c.slug === m.key);
                const title = m.label || catalogEntry?.[lang === "ar" ? "labelAr" : "labelEn"] || (meta ? (lang === "ar" ? meta.labelAr : meta.labelEn) : m.key);
                // Prefer the platform-managed catalog image when available
                // (official logo). Fall back to the built-in <PaymentBrand> chip
                // for known keys with no catalog image, then to tenant uploads
                // for unknown custom keys.
                const resolvedImage = catalogEntry?.imageUrl || (meta ? "" : m.imageUrl) || "";
                if (resolvedImage) {
                  return (
                    <img
                      key={`${m.key}-${idx}`}
                      src={resolvedImage}
                      alt={title}
                      title={title}
                      loading="lazy"
                      decoding="async"
                      width={Math.round(iconSize * 1.6)}
                      height={iconSize}
                      className="object-contain rounded-md bg-white p-1 shadow-sm"
                      style={{ height: iconSize, maxWidth: iconSize * 2.4 }}
                    />
                  );
                }
                return <PaymentBrand key={`${m.key}-${idx}`} brand={m.key} height={iconSize} title={title} />;
              })}
            </div>
          );
        })()}

        <TrustStrip lang={lang} />
        <p className="text-sm text-center pt-4 pb-2">{copyrightText}</p>
      </div>

      <PlatformBadge lang={lang} />
      <PlatformProviderJsonLd
        storeName={storeName}
        storeUrl={typeof window !== "undefined" ? window.location.origin : undefined}
      />
    </footer>
  );
}
